import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, serverTimestamp, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';

const PurchaseInvoice = ({ onBack, editingInvoice }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [supplierDetails, setSupplierDetails] = useState({
    mrnNo: 1,
    poNo: '',
    supplier: '',
    address: '',
    city: '',
    pincode: '',
    terms: 'CASH',
    creditDays: '',
    invoiceNo: '1',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentStatus: 'PENDING',
  });
  const [items, setItems] = useState([
    {
      id: 1,
      item: '',
      ptrWholesaleRate: 0,
      mrp: 0,
      sRate: 0,
      pRate: 0,
      unit: '',
      qty: 0,
      discPercent: 0,
      discAmt: 0,
      cgstPercent: 0,
      sgstPercent: 0,
      cessPercent: 0,
      cgstAmt: 0,
      sgstAmt: 0,
      cessAmt: 0,
      unitPrice: 0,
      netValue: 0,
    },
  ]);
  const [summary, setSummary] = useState({
    grossAmt: 0,
    cgstAmt: 0,
    sgstAmt: 0,
    igstAmt: 0,
    cessAmt: 0,
    discount: 0,
    dutyFreightOthers: 0,
    cashDiscount: 0,
    roundedOff: 0,
    netAmt: 0,
  });
  const [isIgstChecked, setIsIgstChecked] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    mobile: '',
    address1: '',
    address2: '',
    country: 'India',
    state: '',
    city: '',
    zipcode: '',
    gstin: '',
    email: '',
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [products, setProducts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Validation function for pincode
  const validatePincode = (value) => /^\d{0,6}$/.test(value);

  // Validation function for email
  const validateEmail = (value) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

  useEffect(() => {
    if (!currentUser || editingInvoice) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    get(invoicesRef)
      .then((snapshot) => {
        let maxInvoiceNo = 0;
        if (snapshot.exists()) {
          const invoices = snapshot.val();
          const invoiceNos = Object.values(invoices)
            .map((invoice) => parseInt(invoice.supplierDetails.invoiceNo, 10))
            .filter((num) => !isNaN(num));
          maxInvoiceNo = invoiceNos.length > 0 ? Math.max(...invoiceNos) : 0;
        }
        setSupplierDetails((prev) => ({ ...prev, invoiceNo: (maxInvoiceNo + 1).toString() }));
      })
      .catch((error) => {
        console.error('Error fetching Invoice No:', error);
        alert('Failed to fetch Invoice No.');
      });
  }, [currentUser, editingInvoice]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser || editingInvoice) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    get(invoicesRef)
      .then((snapshot) => {
        let maxMrnNo = 0;
        if (snapshot.exists()) {
          const invoices = snapshot.val();
          const mrnNos = Object.values(invoices)
            .map((invoice) => parseInt(invoice.supplierDetails.mrnNo, 10))
            .filter((num) => !isNaN(num));
          maxMrnNo = mrnNos.length > 0 ? Math.max(...mrnNos) : 0;
        }
        setSupplierDetails((prev) => ({ ...prev, mrnNo: maxMrnNo + 1 || 1 }));
      })
      .catch((error) => {
        console.error('Error fetching MRN No:', error);
        alert('Failed to fetch MRN No.');
      });
  }, [currentUser, editingInvoice]);

  useEffect(() => {
    if (editingInvoice) {
      setSupplierDetails({
        mrnNo: editingInvoice.supplierDetails.mrnNo || 1,
        poNo: editingInvoice.supplierDetails.poNo || '',
        supplier: editingInvoice.supplierDetails.supplier || '',
        address: editingInvoice.supplierDetails.address || '',
        city: editingInvoice.supplierDetails.city || '',
        pincode: editingInvoice.supplierDetails.cityPincode || '',
        terms: editingInvoice.supplierDetails.terms || 'CASH',
        creditDays: editingInvoice.supplierDetails.creditDays || '',
        invoiceNo: editingInvoice.supplierDetails.invoiceNo || '',
        invoiceDate: editingInvoice.supplierDetails.invoiceDate || '',
        dueDate: editingInvoice.supplierDetails.dueDate || '',
        paymentStatus: editingInvoice.supplierDetails.paymentStatus || 'PENDING',
      });
      setItems(
        editingInvoice.items.length > 0
          ? editingInvoice.items.map((item, index) => ({
              ...item,
              id: item.id || index + 1,
              unit: item.unit || '',
              ptrWholesaleRate: parseFloat(item.ptrWholesaleRate) || 0,
              mrp: parseFloat(item.mrp) || 0,
              sRate: parseFloat(item.sRate) || 0,
              pRate: parseFloat(item.pRate) || 0,
              qty: parseFloat(item.qty) || 0,
              discPercent: parseFloat(item.discPercent) || 0,
              discAmt: parseFloat(item.discAmt) || 0,
              cgstPercent: parseFloat(item.cgstPercent) || 0,
              sgstPercent: parseFloat(item.sgstPercent) || 0,
              cessPercent: parseFloat(item.cessPercent) || 0,
              cgstAmt: parseFloat(item.cgstAmt) || 0,
              sgstAmt: parseFloat(item.sgstAmt) || 0,
              cessAmt: parseFloat(item.cessAmt) || 0,
              unitPrice: parseFloat(item.unitPrice) || 0,
              netValue: parseFloat(item.netValue) || 0,
            }))
          : [
              {
                id: 1,
                item: '',
                ptrWholesaleRate: 0,
                mrp: 0,
                sRate: 0,
                pRate: 0,
                unit: '',
                qty: 0,
                discPercent: 0,
                discAmt: 0,
                cgstPercent: 0,
                sgstPercent: 0,
                cessPercent: 0,
                cgstAmt: 0,
                sgstAmt: 0,
                cessAmt: 0,
                unitPrice: 0,
                netValue: 0,
              },
            ]
      );
      setSummary({
        grossAmt: parseFloat(editingInvoice.summary.grossAmt) || 0,
        cgstAmt: parseFloat(editingInvoice.summary.cgstAmt) || 0,
        sgstAmt: parseFloat(editingInvoice.summary.sgstAmt) || 0,
        igstAmt: parseFloat(editingInvoice.summary.igstAmt) || 0,
        cessAmt: parseFloat(editingInvoice.summary.cessAmt) || 0,
        discount: parseFloat(editingInvoice.summary.discount) || 0,
        dutyFreightOthers: parseFloat(editingInvoice.summary.dutyFreightOthers) || 0,
        cashDiscount: parseFloat(editingInvoice.summary.cashDiscount) || 0,
        roundedOff: parseFloat(editingInvoice.summary.roundedOff) || 0,
        netAmt: parseFloat(editingInvoice.summary.netAmt) || 0,
      });
      setIsIgstChecked(editingInvoice.summary.igstAmt > 0);
    }
  }, [editingInvoice]);

  useEffect(() => {
    if (currentUser) {
      fetchSuppliers();
      fetchProducts();
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchCountriesAndStates = async () => {
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states');
        const data = await response.json();
        if (data.error) {
          console.error('API Error:', data.msg);
          alert('Failed to load countries and states.');
          return;
        }
        setApiData(data.data);
        setCountries(data.data.map((item) => item.name));
      } catch (error) {
        console.error('Error fetching countries and states:', error);
        alert('Failed to load countries and states.');
      }
    };
    fetchCountriesAndStates();
  }, []);

  useEffect(() => {
    if (newSupplier.country && apiData) {
      const selectedCountryData = apiData.find((country) => country.name === newSupplier.country);
      if (selectedCountryData && selectedCountryData.states) {
        setStates(selectedCountryData.states.map((state) => state.name));
      } else {
        setStates([]);
      }
      setNewSupplier((prev) => ({ ...prev, state: '' }));
    }
  }, [newSupplier.country, apiData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('input[name="item"]') && !e.target.closest('.suggestions-table')) {
        setSuggestions([]);
        setActiveRowId(null);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchSuppliers = async () => {
    if (!currentUser) return;
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
      onValue(suppliersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const supplierList = Object.keys(data).map((key) => ({
            id: key,
            name: data[key].supplierName,
            address: data[key].address || '',
            city: data[key].city || '',
            pincode: data[key].zipCode || '',
          }));
          setSuppliers(supplierList);
        } else {
          setSuppliers([]);
        }
      });
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      alert('Failed to fetch suppliers.');
    }
  };

  const fetchProducts = async () => {
    if (!currentUser) return;
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const productsRef = ref(database, `users/${encodedEmail}/products`);
      onValue(productsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const productList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setProducts(productList);
        } else {
          setProducts([]);
        }
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Failed to fetch products.');
    }
  };

  const handleSupplierChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pincode' && value && !validatePincode(value)) {
      alert('Pin Code must contain only digits (up to 6).');
      return;
    }
    setSupplierDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'supplier') {
      const selectedSupplier = suppliers.find((s) => s.name === value);
      if (selectedSupplier) {
        setSupplierDetails((prev) => ({
          ...prev,
          address: selectedSupplier.address || '',
          city: selectedSupplier.city || '',
          pincode: selectedSupplier.pincode || '',
        }));
      } else {
        setSupplierDetails((prev) => ({
          ...prev,
          address: '',
          city: '',
          pincode: '',
        }));
      }
    }
  };

const calculateItemValues = (item, isIgst = isIgstChecked) => {
  const qty = parseFloat(item.qty) || 0;
  let pRate = parseFloat(item.pRate) || 0;
  const discPercent = parseFloat(item.discPercent) || 0;
  const cgstPercent = parseFloat(item.cgstPercent) || 0;
  const sgstPercent = parseFloat(item.sgstPercent) || 0;
  const cessPercent = parseFloat(item.cessPercent) || 0;

  const basePrice = pRate * qty;
  const discount = (basePrice * discPercent) / 100;
  const taxableValue = basePrice - discount;

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;

  if (isIgst) {
    const combinedTaxRate = cgstPercent + sgstPercent;
    igstAmount = (taxableValue * combinedTaxRate) / 100;
  } else {
    cgstAmount = (taxableValue * cgstPercent) / 100;
    sgstAmount = (taxableValue * sgstPercent) / 100;
  }

  const cessAmount = (taxableValue * cessPercent) / 100;

  const taxMultiplier = 1 + ((isIgst ? (cgstPercent + sgstPercent) : cgstPercent + sgstPercent) + cessPercent) / 100;
  let unitPrice = pRate * taxMultiplier;

  const netValue = taxableValue + cgstAmount + sgstAmount + igstAmount + cessAmount;

  return {
    ...item,
    discAmt: isNaN(discount) ? 0 : parseFloat(discount.toFixed(2)),
    cgstAmt: isNaN(cgstAmount) ? 0 : parseFloat(cgstAmount.toFixed(2)),
    sgstAmt: isNaN(sgstAmount) ? 0 : parseFloat(sgstAmount.toFixed(2)),
    igstAmt: isNaN(igstAmount) ? 0 : parseFloat(igstAmount.toFixed(2)),
    cessAmt: isNaN(cessAmount) ? 0 : parseFloat(cessAmount.toFixed(2)),
    unitPrice: isNaN(unitPrice) ? 0 : parseFloat(unitPrice.toFixed(2)),
    netValue: isNaN(netValue) ? 0 : parseFloat(netValue.toFixed(2)),
  };
};

const calculateSummary = (itemsToCalculate = items, isIgst = isIgstChecked) => {
  const grossAmt = itemsToCalculate.reduce((sum, item) => {
    const qty = parseFloat(item.qty) || 0;
    const pRate = parseFloat(item.pRate) || 0;
    return sum + pRate * qty;
  }, 0);

  const discount = itemsToCalculate.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0);
  const cgstAmt = isIgst ? 0 : itemsToCalculate.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const sgstAmt = isIgst ? 0 : itemsToCalculate.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || 0), 0);
  const igstAmt = isIgst ? itemsToCalculate.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0) : 0;
  const cessAmt = itemsToCalculate.reduce((sum, item) => sum + (parseFloat(item.cessAmt) || 0), 0);

  const netAmt =
    grossAmt -
    discount +
    cgstAmt +
    sgstAmt +
    igstAmt +
    cessAmt +
    (parseFloat(summary.dutyFreightOthers) || 0) -
    (parseFloat(summary.cashDiscount) || 0) +
    (parseFloat(summary.roundedOff) || 0);

  setSummary((prev) => ({
    ...prev,
    grossAmt: parseFloat(grossAmt.toFixed(2)),
    discount: parseFloat(discount.toFixed(2)),
    cgstAmt: parseFloat(cgstAmt.toFixed(2)),
    sgstAmt: parseFloat(sgstAmt.toFixed(2)),
    igstAmt: parseFloat(igstAmt.toFixed(2)),
    cessAmt: parseFloat(cessAmt.toFixed(2)),
    netAmt: parseFloat(netAmt.toFixed(2)),
  }));
};

  const handleIgstChange = (e) => {
    const checked = e.target.checked;
    setIsIgstChecked(checked);

    setItems((prevItems) => {
      const updatedItems = prevItems.map((item) => calculateItemValues(item, checked));
      calculateSummary(updatedItems, checked);
      return updatedItems;
    });
  };

 
  const handleItemChange = (id, field, value) => {
    setItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      );

      if (field === 'item') {
        const query = value.toLowerCase();
        const filtered = products.filter((product) =>
          product.productName && product.productName.toLowerCase().includes(query)
        );
        setSuggestions(filtered.slice(0, 5));
        setActiveRowId(id);
        setActiveSuggestionIndex(-1);
      } else if (field === 'item' && !value.trim()) {
        setSuggestions([]);
        setActiveRowId(null);
        setActiveSuggestionIndex(-1);
      }

      const itemIndex = updatedItems.findIndex((i) => i.id === id);
      if (itemIndex !== -1) {
        if (field === 'discPercent') {
          updatedItems[itemIndex] = { ...updatedItems[itemIndex], discAmt: 0 };
        }
        updatedItems[itemIndex] = calculateItemValues(updatedItems[itemIndex], isIgstChecked);
      }
      calculateSummary(updatedItems, isIgstChecked);
      return updatedItems;
    });
  };

 const handleSelectProduct = (product, id) => {
    if (parseInt(product.openingStock || 0) === 0) {
      alert('Cannot add item: Stock is 0.');
      return;
    }
    setItems((prevItems) => {
      const purchaseTax = parseFloat(product.purchaseTax) || 0;
      const cessTax = parseFloat(product.cessTax) || 0;
      const updatedItems = prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              item: product.productName || '',
              ptrWholesaleRate: parseFloat(product.purchaseRateExclTax) || 0,
              mrp: parseFloat(product.mrp) || 0,
              sRate: parseFloat(product.sellingRate) || 0,
              pRate: parseFloat(product.purchaseRateExclTax) || 0,
              unit: product.unit || '',
              cgstPercent: isIgstChecked ? 0 : purchaseTax / 2,
              sgstPercent: isIgstChecked ? 0 : purchaseTax / 2,
              igstAmt: isIgstChecked ? (parseFloat(product.purchaseRateExclTax) || 0) * (purchaseTax / 100) : 0,
              cessPercent: cessTax,
              discAmt: 0,
              cgstAmt: 0,
              sgstAmt: 0,
              cessAmt: 0,
              unitPrice: 0,
              netValue: 0,
              barcode: product.barcode || '',
            }
          : item
      );
      const itemIndex = updatedItems.findIndex((i) => i.id === id);
      if (itemIndex !== -1) {
        updatedItems[itemIndex] = calculateItemValues(updatedItems[itemIndex], isIgstChecked);
      }
      calculateSummary(updatedItems, isIgstChecked);
      return updatedItems;
    });
    setSuggestions([]);
    setActiveRowId(null);
    setActiveSuggestionIndex(-1);
  };

  const handleKeyDown = (e, id) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectProduct(suggestions[activeSuggestionIndex], id);
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveRowId(null);
      setActiveSuggestionIndex(-1);
    }
  };

  const addNewLine = () => {
    const newId = items.length > 0 ? Math.max(...items.map((item) => item.id)) + 1 : 1;
    setItems((prev) => [
      ...prev,
      {
        id: newId,
        item: '',
        ptrWholesaleRate: 0,
        mrp: 0,
        sRate: 0,
        pRate: 0,
        unit: '',
        qty: 0,
        discPercent: 0,
        discAmt: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        cessPercent: 0,
        cgstAmt: 0,
        sgstAmt: 0,
        cessAmt: 0,
        unitPrice: 0,
        netValue: 0,
      },
    ]);
  };

  const deleteItem = (id) => {
    if (!window.confirm('Are you sure you want to delete the data or item?')) {
      return;
    }
    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);
    calculateSummary(updatedItems);
  };

  const handleSummaryChange = (field, value) => {
    setSummary((prev) => {
      const updatedSummary = {
        ...prev,
        [field]: parseFloat(value) || 0,
      };

      const netAmt =
        updatedSummary.grossAmt -
        updatedSummary.discount +
        (isIgstChecked ? 0 : updatedSummary.cgstAmt) +
        (isIgstChecked ? 0 : updatedSummary.sgstAmt) +
        updatedSummary.cessAmt +
        updatedSummary.igstAmt +
        updatedSummary.dutyFreightOthers -
        updatedSummary.cashDiscount +
        updatedSummary.roundedOff;

      return { ...updatedSummary, netAmt };
    });
  };

  const saveInvoice = async () => {
    if (!currentUser) {
      alert('Please login to save invoice');
      return;
    }

    if (!supplierDetails.invoiceNo) {
      alert('Invoice number is required');
      return;
    }

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);

      const snapshot = await get(invoicesRef);
      let isDuplicate = false;
      if (snapshot.exists()) {
        const invoices = snapshot.val();
        Object.keys(invoices).forEach((key) => {
          const invoice = invoices[key];
          if (
            invoice.supplierDetails.invoiceNo === supplierDetails.invoiceNo &&
            (!editingInvoice || (editingInvoice && key !== editingInvoice.id))
          ) {
            isDuplicate = true;
          }
        });
      }

      if (isDuplicate) {
        alert('Invoice number already exists. Please use a different invoice number.');
        return;
      }

      const netAmt = parseFloat(summary.netAmt.toFixed(2)) || 0;
      
      const totalPaid = editingInvoice ? (parseFloat(editingInvoice.summary?.totalPaid) || 0) : 0;
      const outstanding = Math.max(0, netAmt - totalPaid);
      
      let paymentStatus;
      if (!editingInvoice) {
        paymentStatus = 'Pending';
      } else {
        paymentStatus = outstanding <= 0.01 ? 'Complete' : (totalPaid > 0 ? 'Partial' : 'Pending');
      }

      const invoiceData = {
        userEmail: currentUser.email,
        supplierDetails: {
          ...supplierDetails,
          paymentStatus: paymentStatus,
        },
        items: items.map((item) => ({
          ...item,
          netValue: parseFloat(item.netValue.toFixed(2)),
        })),
        summary: {
          ...summary,
          grossAmt: parseFloat(summary.grossAmt.toFixed(2)),
          cgstAmt: parseFloat(summary.cgstAmt.toFixed(2)),
          sgstAmt: parseFloat(summary.sgstAmt.toFixed(2)),
          igstAmt: parseFloat(summary.igstAmt.toFixed(2)),
          cessAmt: parseFloat(summary.cessAmt.toFixed(2)),
          discount: parseFloat(summary.discount.toFixed(2)),
          dutyFreightOthers: parseFloat(summary.dutyFreightOthers.toFixed(2)),
          cashDiscount: parseFloat(summary.cashDiscount.toFixed(2)),
          roundedOff: parseFloat(summary.roundedOff.toFixed(2)),
          netAmt: netAmt,
          totalPaid: totalPaid,
          outstanding: outstanding,
          paymentStatus: paymentStatus,
        },
        createdAt: editingInvoice ? editingInvoice.createdAt : serverTimestamp(),
        createdBy: currentUser.email,
        updatedAt: serverTimestamp(),
      };

      if (editingInvoice && editingInvoice.id) {
        const invoiceRef = ref(database, `users/${encodedEmail}/invoices/${editingInvoice.id}`);
        await set(invoiceRef, invoiceData);
        alert('Purchase Invoice updated successfully!');
      } else {
        await push(invoicesRef, invoiceData);
        alert('Purchase Invoice saved successfully!');
      }
      onBack();
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice.');
    }
  };

  const cancelInvoice = () => {
    if (!editingInvoice) {
      setSupplierDetails({
        mrnNo: supplierDetails.mrnNo,
        poNo: '',
        supplier: '',
        address: '',
        city: '',
        pincode: '',
        terms: 'CASH',
        creditDays: '',
        invoiceNo: '',
        invoiceDate: '',
        dueDate: '',
        paymentStatus: 'PENDING',
      });
      setItems([
        {
          id: 1,
          item: '',
          ptrWholesaleRate: 0,
          mrp: 0,
          sRate: 0,
          pRate: 0,
          unit: '',
          qty: 0,
          discPercent: 0,
          discAmt: 0,
          cgstPercent: 0,
          sgstPercent: 0,
          cessPercent: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          cessAmt: 0,
          unitPrice: 0,
          netValue: 0,
        },
      ]);
      setSummary({
        grossAmt: 0,
        cgstAmt: 0,
        sgstAmt: 0,
        igstAmt: 0,
        cessAmt: 0,
        discount: 0,
        dutyFreightOthers: 0,
        cashDiscount: 0,
        roundedOff: 0,
        netAmt: 0,
      });
      setIsIgstChecked(false);
    }
    onBack();
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewSupplier({
      name: '',
      mobile: '',
      address1: '',
      address2: '',
      country: 'India',
      state: '',
      city: '',
      zipcode: '',
      gstin: '',
      email: '',
    });
  };

  const handleNewSupplierChange = (e) => {
    const { name, value } = e.target;
    if (name === 'zipcode' && value && !validatePincode(value)) {
      alert('Zip Code must contain only digits (up to 6).');
      return;
    }
    if (name === 'email' && value && !validateEmail(value)) {
      alert('Email should contain only @ as special character and follow proper email format.');
      return;
    }
    setNewSupplier((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addSupplier = async () => {
    if (!currentUser) {
      alert('Please login to add supplier');
      return;
    }

    if (!newSupplier.name || !newSupplier.mobile) {
      alert('Name and Mobile are required fields');
      return;
    }

    if (newSupplier.zipcode && (newSupplier.zipcode.length !== 6 || !/^\d{6}$/.test(newSupplier.zipcode))) {
      alert('Zip Code must be exactly 6 digits.');
      return;
    }

    if (newSupplier.email && !validateEmail(newSupplier.email)) {
      alert('Email should contain only @ as special character and follow proper email format.');
      return;
    }

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const supplierData = {
        supplierName: newSupplier.name,
        mobileNo: newSupplier.mobile,
        address: newSupplier.address1,
        address2: newSupplier.address2,
        country: newSupplier.country,
        state: newSupplier.state,
        city: newSupplier.city,
        zipCode: newSupplier.zipcode,
        gstTinNo: newSupplier.gstin,
        email: newSupplier.email,
        userEmail: currentUser.email,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      };

      const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
      await push(suppliersRef, supplierData);

      alert('Supplier added successfully!');
      closeModal();
    } catch (error) {
      console.error('Error adding supplier:', error);
      alert('Failed to add supplier.');
    }
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '1rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      margin: '0 auto',
      maxWidth: '1400px',
      overflowX: 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2563eb',
    },
    headerButtons: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    backButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    igstCheckboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.8rem',
      color: '#374151',
      fontWeight: '500',
    },
    igstCheckbox: {
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    newProductButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
      width: '2rem',
      height: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1.5rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
    },
    section1: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
      paddingBottom: '100px',
    },
    sectionHeading: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '0.75rem',
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      display: 'block',
      width: '80%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    tableContainer: {
      marginTop: '1rem',
   
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      position: 'relative',
      maxWidth: '100%',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(241,245,249,0.95) 100%)',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.75rem',
      minWidth: '1250px',
    },
    tableHeader: {
      padding: '0.5rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
      whiteSpace: 'nowrap',
    },
    tableCell: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
      position: 'relative',
    },
    tableInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '80px',
      height: '28px',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    readOnlyInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '80px',
      height: '28px',
      backgroundColor: '#f9fafb',
      transition: 'border-color 0.2s ease',
      cursor: 'not-allowed',
    },
    deleteButton: {
      padding: '0.3rem 0.6rem',
      borderRadius: '4px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.7rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    newLineButton: {
      marginTop: '1rem',
      padding: '0.75rem 1.5rem',
      borderRadius: '6px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.85rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    summaryContainer: {
      marginTop: '1.5rem',
      width: '100%',
      maxWidth: '250px',
      marginLeft: 'auto',
    },
    summaryBox: {
      border: '1px solid #e2e8f0',
      padding: '1rem',
      borderRadius: '8px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.5rem',
      fontSize: '0.8rem',
      color: '#374151',
    },
    summaryInput: {
      border: '1px solid #d1d5db',
      padding: '0.4rem',
      width: '40%',
      borderRadius: '4px',
      fontSize: '0.8rem',
      textAlign: 'right',
      backgroundColor: '#f3f4f6',
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: '600',
      borderTop: '1px solid #e2e8f0',
      paddingTop: '0.5rem',
      marginTop: '0.75rem',
      color: '#1f2937',
    },
    actionButtons: {
      marginTop: '1.5rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e2e8f0',
      flexWrap: 'wrap',
    },
    saveButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    cancelButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    modalOverlay: {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: '1000',
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '8px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    modalHeader: {
      backgroundColor: '#2563eb',
      color: 'white',
      padding: '0.75rem 1.25rem',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '-1.5rem -1.5rem 1rem -1.5rem',
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
    },
    modalCloseButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: '1.5rem',
      cursor: 'pointer',
    },
    modalGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '0.75rem',
      marginBottom: '1rem',
    },
    modalActionButtons: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
    },
    modalAddButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    modalCancelButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    suggestions: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '600px',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      zIndex: 1000,
      maxHeight: '300px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
    },
    suggestionTable: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    suggestionTh: {
      padding: '0.5rem',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textAlign: 'center',
      border: '1px solid #1e40af',
    },
    suggestionTd: {
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      fontSize: '0.75rem',
      textAlign: 'center',
    },
    suggestionItem: {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    suggestionActive: {
      backgroundColor: '#e0f2fe',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#718096', fontSize: '1rem' }}>
          <h2>Please login to access the Purchase Invoice</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            {editingInvoice ? 'Edit Purchase Invoice' : 'Purchase Invoice'}
          </div>
          <div style={styles.headerButtons}>
            <button
              onClick={onBack}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
            >
              ← Back
            </button>
            <label style={styles.igstCheckboxLabel}>
              <input
                type="checkbox"
                checked={isIgstChecked}
                onChange={handleIgstChange}
                style={styles.igstCheckbox}
              />
              Apply IGST
            </label>
            <button
              onClick={openModal}
              style={styles.newProductButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
              }}
            >
              +
            </button>
          </div>
        </div>

        <div style={styles.formLayout}>
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📋 Supplier Details</h2>
            <div style={styles.gridContainer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>MRN No</label>
                <input
                  type="text"
                  name="mrnNo"
                  value={supplierDetails.mrnNo}
                  readOnly
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>PO No</label>
                <input
                  type="text"
                  name="poNo"
                  value={supplierDetails.poNo}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Supplier/Vendor</label>
                <select
                  name="supplier"
                  value={supplierDetails.supplier}
                  onChange={handleSupplierChange}
                  style={styles.select}
                >
                  <option value="">Select Supplier/Vendor</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address</label>
                <input
                  type="text"
                  name="address"
                  value={supplierDetails.address}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={supplierDetails.city}
                  onChange={handleSupplierChange}
                  style={styles.input}
             
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pin Code</label>
                <input
                  type="text"
                  name="pincode"
                  value={supplierDetails.pincode}
                  onChange={handleSupplierChange}
                  style={styles.input}
                  maxLength={6}
                  pattern="\d{6}"
                  title="Pin Code must be exactly 6 digits"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Terms</label>
                <select
                  name="terms"
                  value={supplierDetails.terms}
                  onChange={handleSupplierChange}
                  style={styles.select}
                >
                  <option value="CASH">CASH</option>
                  <option value="CREDIT">CREDIT</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Credit Days</label>
                <input
                  type="number"
                  name="creditDays"
                  value={supplierDetails.creditDays}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Invoice No</label>
                <input
                  type="text"
                  name="invoiceNo"
                  value={supplierDetails.invoiceNo}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Invoice Date</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={supplierDetails.invoiceDate}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={supplierDetails.dueDate}
                  onChange={handleSupplierChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          <div style={styles.section1}>
            <h2 style={styles.sectionHeading}>📦 Items</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, width: '50px' }}>S.NO</th>
                    <th style={{ ...styles.tableHeader, width: '220px' }}>ITEM</th>
                    <th style={{ ...styles.tableHeader, width: '90px' }}>PTR/WHOLESALE RATE</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>MRP</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>S.RATE</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>P.RATE</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>UNIT</th>
                    <th style={{ ...styles.tableHeader, width: '60px' }}>QTY</th>
                    <th style={{ ...styles.tableHeader, width: '70px' }}>DISC%</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>DISC. AMT.</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>CGST</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>SGST</th>
                    <th style={{ ...styles.tableHeader, width: '70px' }}>CESS</th>
                    <th style={{ ...styles.tableHeader, width: '110px' }}>UNIT PRICE / PURCHASE RATE (INCL. TAX)</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>NET VALUE</th>
                    <th style={{ ...styles.tableHeader, width: '50px' }}>Action or Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td style={{ ...styles.tableCell, fontWeight: '600', color: '#2563eb' }}>
                        {item.id}
                      </td>
                      <td style={{ ...styles.tableCell, position: 'relative' }}>
                        <input
                          type="text"
                          value={item.item}
                          onChange={(e) => handleItemChange(item.id, 'item', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, item.id)}
                          style={{ ...styles.tableInput, width: '180px' }}
                          placeholder="Search product..."
                          name="item"
                        />
                        {activeRowId === item.id && suggestions.length > 0 && (
                          <div style={styles.suggestions} className="suggestions-table">
                            <table style={styles.suggestionTable}>
                              <thead>
                                <tr>
                                  <th style={styles.suggestionTh}>BARCODE</th>
                                  <th style={styles.suggestionTh}>PRODUCT</th>
                                  <th style={styles.suggestionTh}>RACK</th>
                                  <th style={styles.suggestionTh}>BRAND</th>
                                  <th style={styles.suggestionTh}>P.PRICE</th>
                                  <th style={styles.suggestionTh}>QTY</th>
                                </tr>
                              </thead>
                              <tbody>
                                {suggestions.map((product, idx) => (
                                  <tr
                                    key={product.id}
                                    style={{
                                      ...styles.suggestionItem,
                                      ...(activeSuggestionIndex === idx ? styles.suggestionActive : {}),
                                    }}
                                    onClick={() => handleSelectProduct(product, item.id)}
                                    onMouseEnter={() => setActiveSuggestionIndex(idx)}
                                  >
                                    <td style={styles.suggestionTd}>{product.barcode || 'N/A'}</td>
                                    <td style={styles.suggestionTd}>{product.productName || 'Unknown Product'}</td>
                                    <td style={styles.suggestionTd}>{product.rack || 'N/A'}</td>
                                    <td style={styles.suggestionTd}>{product.brand || 'GENERAL'}</td>
                                    <td style={styles.suggestionTd}>
                                      ₹{parseFloat(product.purchaseRateExclTax || 0).toFixed(2)}
                                    </td>
                                    <td style={styles.suggestionTd}>{product.openingStock || '0'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.ptrWholesaleRate}
                          onChange={(e) => handleItemChange(item.id, 'ptrWholesaleRate', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.mrp}
                          onChange={(e) => handleItemChange(item.id, 'mrp', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.sRate}
                          onChange={(e) => handleItemChange(item.id, 'sRate', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.pRate}
                          onChange={(e) => handleItemChange(item.id, 'pRate', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="text"
                          value={item.unit}
                          onChange={(e) => handleItemChange(item.id, 'unit', e.target.value)}
                          style={styles.tableInput}
                          placeholder="Unit"
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.qty}
                          onChange={(e) => handleItemChange(item.id, 'qty', parseInt(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.discPercent}
                          onChange={(e) => handleItemChange(item.id, 'discPercent', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.discAmt.toFixed(2)}
                          onChange={(e) => handleItemChange(item.id, 'discAmt', parseFloat(e.target.value) || 0)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <div>
                          <input
                            type="number"
                            value={item.cgstPercent}
                            onChange={(e) => handleItemChange(item.id, 'cgstPercent', parseFloat(e.target.value) || 0)}
                            style={{ ...styles.tableInput, marginBottom: '2px' }}
                            placeholder="Rate %"
                          />
                          <input
                            type="number"
                            value={item.cgstAmt.toFixed(2)}
                            readOnly
                            style={styles.readOnlyInput}
                            placeholder="Amount"
                          />
                        </div>
                      </td>
                      <td style={styles.tableCell}>
                        <div>
                          <input
                            type="number"
                            value={item.sgstPercent}
                            onChange={(e) => handleItemChange(item.id, 'sgstPercent', parseFloat(e.target.value) || 0)}
                            style={{ ...styles.tableInput, marginBottom: '2px' }}
                            placeholder="Rate %"
                          />
                          <input
                            type="number"
                            value={item.sgstAmt.toFixed(2)}
                            readOnly
                            style={styles.readOnlyInput}
                            placeholder="Amount"
                          />
                        </div>
                      </td>
                      <td style={styles.tableCell}>
                        <div>
                          <input
                            type="number"
                            value={item.cessPercent}
                            onChange={(e) => handleItemChange(item.id, 'cessPercent', parseFloat(e.target.value) || 0)}
                            style={{ ...styles.tableInput, marginBottom: '2px' }}
                            placeholder="Rate %"
                          />
                          <input
                            type="number"
                            value={item.cessAmt.toFixed(2)}
                            readOnly
                            style={styles.readOnlyInput}
                            placeholder="Amount"
                          />
                        </div>
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.unitPrice.toFixed(2)}
                          readOnly
                          style={styles.readOnlyInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <input
                          type="number"
                          value={item.netValue.toFixed(2)}
                          readOnly
                          style={styles.readOnlyInput}
                        />
                      </td>
                      <td style={styles.tableCell}>
                        <button
                          onClick={() => deleteItem(item.id)}
                          style={styles.deleteButton}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={addNewLine}
              style={styles.newLineButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + New Line
            </button>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📊 Summary</h2>
            <div style={styles.summaryContainer}>
              <div style={styles.summaryBox}>
                <div style={styles.summaryRow}>
                  <span>Gross Amount:</span>
                  <input
                    type="number"
                    value={summary.grossAmt.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>CGST Amt:</span>
                  <input
                    type="number"
                    value={summary.cgstAmt.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>SGST Amt:</span>
                  <input
                    type="number"
                    value={summary.sgstAmt.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>IGST Amt:</span>
                  <input
                    type="number"
                    value={summary.igstAmt.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>CESS Amt:</span>
                  <input
                    type="number"
                    value={summary.cessAmt.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Discount:</span>
                  <input
                    type="number"
                    value={summary.discount.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Duty/Freight/Others:</span>
                  <input
                    type="number"
                    value={summary.dutyFreightOthers.toFixed(2)}
                    onChange={(e) => handleSummaryChange('dutyFreightOthers', e.target.value)}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Cash Discount:</span>
                  <input
                    type="number"
                    value={summary.cashDiscount.toFixed(2)}
                    onChange={(e) => handleSummaryChange('cashDiscount', e.target.value)}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Rounded Off:</span>
                  <input
                    type="number"
                    value={summary.roundedOff.toFixed(2)}
                    onChange={(e) => handleSummaryChange('roundedOff', e.target.value)}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryTotal}>
                  <span>Net Amount:</span>
                  <span>₹{summary.netAmt.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.actionButtons}>
            <button
              onClick={saveInvoice}
              style={styles.saveButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
            >
              ✅ {editingInvoice ? 'Update' : 'Save'}
            </button>
            <button
              onClick={cancelInvoice}
              style={styles.cancelButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
              }}
            >
              ❌ Cancel
            </button>
          </div>
        </div>

        {isModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={styles.modalHeader}>
                <div style={styles.modalTitle}>Add Supplier</div>
                <button onClick={closeModal} style={styles.modalCloseButton}>
                  ×
                </button>
              </div>
              <div style={styles.modalGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Name* <span style={{ fontSize: '0.75rem', color: '#666' }}>[Company/Supplier]</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newSupplier.name}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Mobile*</label>
                  <input
                    type="text"
                    name="mobile"
                    value={newSupplier.mobile}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                    maxLength={10}
                    pattern="\d{10}"
                    title="Mobile must be exactly 10 digits"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Address 1</label>
                  <input
                    type="text"
                    name="address1"
                    value={newSupplier.address1}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Address 2</label>
                  <input
                    type="text"
                    name="address2"
                    value={newSupplier.address2}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Country</label>
                  <select
                    name="country"
                    value={newSupplier.country}
                    onChange={handleNewSupplierChange}
                    style={styles.select}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>State</label>
                  <select
                    name="state"
                    value={newSupplier.state}
                    onChange={handleNewSupplierChange}
                    style={styles.select}
                    disabled={!newSupplier.country || states.length === 0}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={newSupplier.city}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Zip Code</label>
                  <input
                    type="text"
                    name="zipcode"
                    value={newSupplier.zipcode}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                    maxLength={6}
                    pattern="\d{6}"
                    title="Zip Code must be exactly 6 digits"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>GSTIN</label>
                  <input
                    type="text"
                    name="gstin"
                    value={newSupplier.gstin}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={newSupplier.email}
                    onChange={handleNewSupplierChange}
                    style={styles.input}
                    title="Email should contain only @ as special character"
                  />
                </div>
              </div>
              <div style={styles.modalActionButtons}>
                <button
                  onClick={addSupplier}
                  style={styles.modalAddButton}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  + Add
                </button>
                <button
                  onClick={closeModal}
                  style={styles.modalCancelButton}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                  }}
                >
                  × Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseInvoice;